// Audit Logs Web Component

class AuditLogs extends HTMLElement {
  constructor() {
    super();
    this.logs = [];
    this.isLoading = false;
    this.currentPage = 1;
    this.totalPages = 1;
    this.filters = {
      action: '',
      userId: ''
    };
  }

  connectedCallback() {
    this.render();
    this.loadAuditLogs();
    this.attachEventListeners();
  }

  render() {
    this.innerHTML = `
      <div class="audit-logs">
        <div class="audit-header">
          <h2>Audit Logs</h2>
          <div class="audit-controls">
            <div class="filter-group">
              <label for="actionFilter">Filter by Action:</label>
              <select id="actionFilter" class="form-control">
                <option value="">All Actions</option>
                <option value="login">Login</option>
                <option value="logout">Logout</option>
                <option value="register">Register</option>
                <option value="create_post">Create Post</option>
                <option value="update_post">Update Post</option>
                <option value="delete_post">Delete Post</option>
                <option value="send_friend_request">Friend Request</option>
                <option value="accept_friend_request">Accept Friend</option>
                <option value="decline_friend_request">Decline Friend</option>
                <option value="update_user_role">Update User Role</option>
                <option value="delete_user">Delete User</option>
              </select>
            </div>
            
            <div class="filter-group">
              <label for="userFilter">Filter by User ID:</label>
              <input type="number" id="userFilter" class="form-control" placeholder="User ID">
            </div>
            
            <button class="btn btn-primary" id="applyFilters">
              Apply Filters
            </button>
            
            <button class="btn btn-outline" id="clearFilters">
              Clear
            </button>
          </div>
        </div>

        <div class="audit-content">
          ${this.renderContent()}
        </div>

        ${this.renderPagination()}
      </div>
    `;
  }

  renderContent() {
    if (this.isLoading) {
      return `
        <div class="loading-state">
          <div class="spinner"></div>
          <div>Loading audit logs...</div>
        </div>
      `;
    }

    if (this.logs.length === 0) {
      return `
        <div class="empty-state">
          <div class="empty-state-icon">📋</div>
          <h3>No audit logs found</h3>
          <p>Try adjusting your filters or check back later.</p>
        </div>
      `;
    }

    return `
      <div class="log-list">
        ${this.logs.map(log => this.renderLogEntry(log)).join('')}
      </div>
    `;
  }

  renderLogEntry(log) {
    const time = new Date(log.created_at).toLocaleString();
    const details = log.details ? JSON.stringify(log.details, null, 2) : '';

    return `
      <div class="log-entry">
        <div class="log-header">
          <span class="log-action">${log.action.replace(/_/g, ' ')}</span>
          <span class="log-time">${time}</span>
        </div>
        
        ${log.username ? `
          <div class="log-user">
            👤 ${log.username} (ID: ${log.user_id})
          </div>
        ` : ''}
        
        ${details ? `
          <div class="log-details">
            <strong>Details:</strong><br>
            <pre>${details}</pre>
          </div>
        ` : ''}
        
        ${log.ip_address ? `
          <div class="log-ip">
            🌐 ${log.ip_address}
          </div>
        ` : ''}
      </div>
    `;
  }

  renderPagination() {
    if (this.totalPages <= 1) {
      return '';
    }

    const startPage = Math.max(1, this.currentPage - 2);
    const endPage = Math.min(this.totalPages, this.currentPage + 2);
    const pages = [];

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i);
    }

    return `
      <div class="pagination">
        <div class="pagination-info">
          Page ${this.currentPage} of ${this.totalPages}
        </div>
        
        <div class="pagination-controls">
          <button class="pagination-btn" ${this.currentPage === 1 ? 'disabled' : ''} data-page="${this.currentPage - 1}">
            ← Previous
          </button>
          
          ${pages.map(page => `
            <button class="pagination-btn ${page === this.currentPage ? 'active' : ''}" data-page="${page}">
              ${page}
            </button>
          `).join('')}
          
          <button class="pagination-btn" ${this.currentPage === this.totalPages ? 'disabled' : ''} data-page="${this.currentPage + 1}">
            Next →
          </button>
        </div>
      </div>
    `;
  }

  attachEventListeners() {
    this.attachFilterListeners();
    this.attachPaginationListeners();
  }

  attachFilterListeners() {
    // Remove existing listeners to prevent duplicates
    const applyBtn = this.querySelector('#applyFilters');
    const clearBtn = this.querySelector('#clearFilters');

    // Clone and replace elements to remove old event listeners
    if (applyBtn) {
      const newApplyBtn = applyBtn.cloneNode(true);
      applyBtn.parentNode.replaceChild(newApplyBtn, applyBtn);
    }
    if (clearBtn) {
      const newClearBtn = clearBtn.cloneNode(true);
      clearBtn.parentNode.replaceChild(newClearBtn, clearBtn);
    }

    // Re-query the new elements
    const newApplyBtn = this.querySelector('#applyFilters');
    const newClearBtn = this.querySelector('#clearFilters');
    const newActionFilter = this.querySelector('#actionFilter');
    const newUserFilter = this.querySelector('#userFilter');

    newApplyBtn?.addEventListener('click', () => {
      this.filters.action = newActionFilter?.value || '';
      this.filters.userId = newUserFilter?.value || '';
      this.currentPage = 1;
      this.loadAuditLogs();
    });

    newClearBtn?.addEventListener('click', () => {
      this.filters.action = '';
      this.filters.userId = '';
      newActionFilter.value = '';
      newUserFilter.value = '';
      this.currentPage = 1;
      this.loadAuditLogs();
    });
  }

  attachPaginationListeners() {
    // Pagination event delegation - this should work even after content updates
    this.addEventListener('click', (e) => {
      if (e.target.classList.contains('pagination-btn') && !e.target.disabled) {
        const page = parseInt(e.target.dataset.page);
        if (page && page !== this.currentPage) {
          this.currentPage = page;
          this.loadAuditLogs();
        }
      }
    });
  }

  async loadAuditLogs() {
    this.isLoading = true;
    this.updateContent();

    try {
      const params = new URLSearchParams({
        page: this.currentPage,
        limit: 20,
        ...this.filters
      });

      const response = await fetch(`/api/admin/audit-logs?${params}`, {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        this.logs = data.logs;
        this.totalPages = data.pagination.pages;
        this.currentPage = data.pagination.page;
      } else {
        throw new Error('Failed to fetch audit logs');
      }
    } catch (error) {
      window.logger.error('Failed to load audit logs:', error);
      this.logs = [];
      this.showError('Failed to load audit logs. Please try again.');
    } finally {
      this.isLoading = false;
      this.updateContent();
    }
  }

  updateContent() {
    const content = this.querySelector('.audit-content');
    if (content) {
      content.innerHTML = this.renderContent();
    }

    const pagination = this.querySelector('.pagination');
    if (pagination) {
      pagination.outerHTML = this.renderPagination();
    }

    // Re-attach event listeners to filter controls (they might have been re-rendered)
    this.attachFilterListeners();
  }

  showError(message) {
    const content = this.querySelector('.audit-content');
    if (content) {
      content.innerHTML = `
        <div class="empty-state">
          <div class="empty-state-icon">❌</div>
          <h3>Error</h3>
          <p>${message}</p>
        </div>
      `;
    }
  }
}

// Register the custom element
customElements.define('audit-logs', AuditLogs);
